import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles, X, SendHorizonal, Volume2, VolumeX } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "cafexir-concierge-session";

function getSessionId() {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "anon";
  }
}

export default function Concierge() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => [{ role: "assistant", content: t("concierge.greeting") }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const audioRef = useRef(null);
  const listRef = useRef(null);

  const speak = async (text, i) => {
    if (speakingIdx === i) {
      audioRef.current?.pause();
      setSpeakingIdx(null);
      return;
    }
    audioRef.current?.pause();
    setSpeakingIdx(i);
    try {
      const res = await fetch(`${API}/concierge/speak`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const audio = new Audio(URL.createObjectURL(blob));
      audioRef.current = audio;
      audio.onended = () => setSpeakingIdx(null);
      await audio.play();
    } catch {
      setSpeakingIdx(null);
    }
  };

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/concierge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = part.slice(6);
          if (payload === "[DONE]") break;
          try {
            const obj = JSON.parse(payload);
            if (obj.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + obj.delta };
                return copy;
              });
            }
            if (obj.error) throw new Error(obj.error);
          } catch (e) {
            if (e.message === "guide_unavailable") throw e;
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: t("concierge.unavailable") };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const suggestions = t("concierge.suggestions");

  return (
    <>
      <motion.button
        onClick={() => setOpen((o) => !o)}
        data-testid="concierge-toggle"
        aria-label={t("concierge.open")}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-6 end-6 z-[65] flex h-13 w-13 items-center justify-center rounded-full border border-xir-gold/40 bg-xir-dark/80 text-xir-gold shadow-[0_10px_30px_rgba(0,0,0,0.45)] backdrop-blur-md"
        style={{ height: 52, width: 52 }}
      >
        {open ? <X size={19} strokeWidth={1.5} /> : <Sparkles size={19} strokeWidth={1.5} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.section
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 end-4 z-[65] flex h-[520px] max-h-[68vh] w-[min(400px,92vw)] flex-col overflow-hidden border hairline bg-xir-surface sm:end-6"
            data-testid="concierge-panel"
            role="dialog"
            aria-label={t("concierge.title")}
          >
            <header className="flex items-center justify-between border-b hairline px-5 py-4">
              <div>
                <p className="font-display text-lg font-medium text-xir-paper" data-testid="concierge-title">
                  {t("concierge.title")}
                </p>
                <p className="text-[0.6rem] uppercase tracking-[0.25em] text-xir-gold">CAFÉXIR</p>
              </div>
              <Sparkles size={16} className="text-xir-gold/60" />
            </header>

            <div ref={listRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-5" data-testid="concierge-messages">
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={msg.role === "user" ? "" : "flex flex-col items-start gap-1"}>
                    <p
                      className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                        msg.role === "user"
                          ? "bg-xir-gold text-xir-dark"
                          : "border hairline text-xir-paper/90"
                      }`}
                      data-testid={`concierge-message-${i}`}
                    >
                      {msg.content}
                      {busy && i === messages.length - 1 && msg.role === "assistant" && (
                        <span className="ms-1 inline-block h-3 w-1 animate-pulse bg-xir-gold" />
                      )}
                    </p>
                    {msg.role === "assistant" && msg.content && (
                      <button
                        onClick={() => speak(msg.content, i)}
                        data-testid={`concierge-speak-${i}`}
                        aria-label={t("concierge.listen")}
                        className="ms-1 text-xir-paper/40 transition-colors hover:text-xir-gold"
                      >
                        {speakingIdx === i ? <Volume2 size={13} /> : <VolumeX size={13} className="opacity-60" />}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {messages.length <= 1 && (
              <div className="flex flex-wrap gap-2 px-5 pb-3">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => send(s)}
                    data-testid={`concierge-suggestion-${i}`}
                    className="border hairline px-3 py-1.5 text-[0.7rem] text-xir-paper/70 transition-colors hover:border-xir-gold/50 hover:text-xir-gold"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-center gap-3 border-t hairline px-4 py-3"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t("concierge.placeholder")}
                data-testid="concierge-input"
                className="flex-1 bg-transparent text-sm text-xir-paper placeholder:text-xir-paper/35 focus:outline-none"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                data-testid="concierge-send"
                aria-label={t("concierge.send")}
                className="text-xir-gold transition-opacity disabled:opacity-30"
              >
                <SendHorizonal size={17} className="rtl:-scale-x-100" />
              </button>
            </form>
          </motion.section>
        )}
      </AnimatePresence>
    </>
  );
}
